import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormsModule } from '@angular/forms';
import { ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { TranslateService } from '../../../../services/translate.service';
import { Address } from '../../../../models/user.model';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { MaintainAccountService } from '../maintain-account.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, takeUntil } from 'rxjs';
import { AddressesComponent } from '../../create-account/steps/addresses/addresses.component';

import { Pagination } from '../../../../services/pagination.abstruct';

@Component({
	selector: 'app-tab-addresses',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		TableModule,
		TagModule,
		ButtonModule,
		ProgressSpinnerModule,
		TooltipModule
	],
	templateUrl: './tab-addresses.component.html',
	styleUrls: ['./tab-addresses.component.scss'],
	providers: [DialogService, MessageService]

})
export class TabAddressesComponent extends Pagination implements OnInit {
	@Input() addresses!: any[];
	@Input() addressesLoading!: boolean;
	@Input() addressesError!: boolean;

	@Input() customerRecId!: number;

	@ViewChild('dt') dt!: Table;

	ref: DynamicDialogRef | undefined;
	private destroy$ = new Subject<void>();

	globalFilter: string = '';
	globalFilterFields: string[] = [];

	constructor(public translateServ: TranslateService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private maintainAccountService: MaintainAccountService,
		private dialogService: DialogService) 
		{
			super();
		 }

	ngOnInit(): void {
		if (this.addresses?.length > 0) {
			this.globalFilterFields = Object.keys(this.addresses[0]);
		}
	}

	ngOnDestroy(): void {
		if (this.ref) {
			this.ref.close();
		}
	}

	getAddressSeverity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger';
	}


	/**
	 * Function to update the status of an address via an API call.
	 * @param recId The RecID of the address to update.
	 * @param newStatus The new status (1 for active, 0 for inactive).
	 */
	updateAddressStatus(source: boolean, recId: number, newStatus: 0 | 1): void {
		// Calling the API service to update the status
		this.maintainAccountService.updateFunctions(source, this.constructor.name.substring(1), recId, newStatus).subscribe({
			next: (response: any) => {
				// Check if the API response indicates a successful update
				if (response && response[0]?.RecID && response[0].RecID !== -1) {
					const index = this.addresses?.findIndex(name => name.RecID === recId);
					if (this.addresses && index !== undefined && index > -1) {
						// Correctly updating the 'IsActive' property or the 'IsApproved'.
						if (source) {
							this.addresses[index].IsActive = newStatus;
						}
						else {
							this.addresses[index].IsApproved = newStatus;
						}
						const statusText = newStatus === 1 ? 'Active' : 'Inactive';
						this.messageService.add({
							severity: 'success',
							summary: this.translateServ.dictionary['STATUS_UPDATED'] || 'Status Updated',
							// Correctly using the 'Street' property for the message
							detail: `Address "${this.addresses[index].Street}" is now ${statusText}.`,
							life: 3000
						});
					}
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Record not found or invalid response.',
						life: 5000
					});
				}
			},
			error: (err: any) => {
				console.error('API Error:', err);
				this.messageService.add({
					severity: 'error',
					summary: this.translateServ.dictionary['ERROR'] || 'Error',
					detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Please try again.',
					life: 5000
				});
			}
		});
	}

	toggleAddressStatus(address: Address, action: boolean): void {
		const newStatus = action ? (address.IsActive === 1 ? 0 : 1) : (address.IsApproved === 1 ? 0 : 1);
		const actionText = newStatus === 1 ?
			(this.translateServ.dictionary['ACTIVATE'] || 'activate') :
			(this.translateServ.dictionary['DEACTIVATE'] || 'deactivate');

		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['CONFIRM_CHANGE_STATUS'] || 'Are you sure you want to'} ${actionText} ${address.Street}?`,
			header: this.translateServ.dictionary['CONFIRM_ACTION'] || 'Confirm Action',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['YES'] || 'Yes',
			rejectLabel: this.translateServ.dictionary['NO'] || 'No',
			accept: () => {
				this.updateAddressStatus(action, address.RecID, newStatus);
			},
			reject: () => {
				this.messageService.add({
					severity: 'info',
					summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled',
					detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Action cancelled.',
					life: 3000
				});
			}
		});
	}


	editAddress(addr: Address): void {
		this.ref = this.dialogService.open(AddressesComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE_EDIT'] || 'Edit Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: false,
				customerRecId: this.customerRecId,
				nameEntry: {
					id: addr.RecID,
					addrRecId: addr.AddressRecID,
					entrence: addr.Entrence,
					addressTypeRecId: addr.AddressTypeRecID,
					addrType: { label: addr.AddressType || null, code: addr.AddressTypeRecID || null, value: addr.AddressType || null },
					streetRecID: addr.StreetRecID,
					street: { label: addr.Street || null, code: addr.StreetRecID || null, value: addr.Street || null },
					cityRecId: addr.CityRecID,
					city: { label: addr.City || null, code: addr.CityRecID || null, value: addr.City || null },
					countyRecId: addr.CountryRecID,
					country: { label: addr.Country || null, code: addr.CountryRecID || null, value: addr.Country || null },
					appartment: addr.Appartment,
					houseNumber: addr.House,
					postalCode: addr.PostalCode,
					postalBox: addr.PostalBox,
					description: addr.Descr,
					isActive: addr.IsActive,
					isApproved: addr.IsApproved
				}
			}
		});

		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_UPDATE_SUCCESS_MSG'] || 'Record updated successfully.',
					life: 3000
				});
				this.reloadAddrsForUser();
			}
		});
	}



	createNewRecord(): void {
		this.ref = this.dialogService.open(AddressesComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE'] || 'Create New Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: true,
				customerRecId: this.customerRecId // Pass the ID here
			}
		});

		// Subscribe to the dialog's onClose event
		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			// Check if the dialog was closed with a successful action (e.g., a "save" button click)
			// The AdditionalNamesComponent would need to return a value like 'true' upon success
			// to trigger this.
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_CREATE_SUCCESS_MSG'] || 'Record created successfully.',
					life: 3000
				});
				this.reloadAddrsForUser();
			}
		});
	}

	// Confirms and deletes a record
	confirmDelete(address: Address): void {
		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['MAINT_DELETE_CONFIRM_MSG'] || 'Are you sure you want to delete this record?'} (ID: ${address.RecID})`,
			header: this.translateServ.dictionary['MAINT_DELETE_CONFIRM_HEADER'] || 'Confirm Deletion',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['CONFIRM_DELETE'] || 'Delete',
			rejectLabel: this.translateServ.dictionary['CONFIRM_CANCEL'] || 'Cancel',
			accept: () => {
				this.maintainAccountService.deleteRecord(this.constructor.name.substring(1), address.RecID)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: (response) => {
							this.messageService.add({
								severity: 'success',
								summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
								detail: this.translateServ.dictionary['MAINT_DELETE_SUCCESS_MSG'] || 'Record deleted successfully.',
								life: 3000,
							});
							this.reloadAddrsForUser();
						},
						error: (error) => {
							console.error('Failed to delete record:', error);
							this.messageService.add({
								severity: 'error',
								summary: this.translateServ.dictionary['ERROR'] || 'Error',
								detail: this.translateServ.dictionary['MAINT_DELETE_FAILED_MSG'] || 'Failed to delete record.',
								life: 5000,
							});
						}
					});
			},
			reject: () => {
				// The user clicked "Cancel", so we can just close the dialog.
			}
		});
	}

	// Reloads the data for the additional names table
	private reloadAddrsForUser(): void {
		// Call the service method to get the updated data
		const index =  this.maintainAccountService.getMappingIndex("addresses");
		const instance = this.maintainAccountService.getUserDataMappings();
		this.maintainAccountService.getDatas(this.customerRecId, instance[index].apiName, instance[index].special)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					this.addresses = data;
					this.addressesLoading = false;
				},
				error: (error) => {
					console.error('Failed to reload addresses: ', error);
					this.addressesError = true;
					this.addressesLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['MAINT_LOAD_FAILED_MSG_ADDR'] || 'Failed to load addresses.',
						life: 5000
					});
				}
			});
	}

	applyGlobalFilter(): void {
		this.dt.filterGlobal(this.globalFilter, 'contains');
	}
}
